import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Switch } from '@/components/ui/Switch';
import { electronApis } from '@/features/electron-api';
import {
  FullDiskAccessHint,
  isFullDiskAccessSetupFailure,
} from '@/features/local-mcp';
import { useSetupProgress } from '@/features/local-mcp/use-setup-progress';
import { useMcpServersQuery } from '@/features/settings/sub-settings/settings-local-mcp.query';
import { fullDiskAccessPermissionQueryOptions } from '@/query/permissions';

import {
  useMailAccountsQuery,
  useMailAccountToggle,
} from './apple-mail-accounts';
import { ServerSetupSection } from './server-setup-section';

import type { ConfigurationContext } from './configuration.types';
import type {
  MailAccount,
  MailAccountsResult,
} from '@electron/shared/mail-accounts.types';

const LIST_FRAME =
  'rounded-lg border border-border bg-surface dark:border-border-strong';

function accountTitle(account: MailAccount): string {
  return account.name ?? 'Mail account';
}

function accountDetail(account: MailAccount): string {
  if (account.emails.length > 0) return account.emails.join(', ');
  return account.name ? '' : account.id.slice(0, 8);
}

function ListMessage({ children }: { children: React.ReactNode }) {
  return (
    <p className={`${LIST_FRAME} px-4 py-3 text-xs text-foreground-muted`}>
      {children}
    </p>
  );
}

function MailAccountsList({
  query,
  blockedIds,
  saving,
  onToggle,
}: {
  query: UseQueryResult<MailAccountsResult>;
  blockedIds: Set<string>;
  saving: boolean;
  onToggle: (accountId: string, enabled: boolean) => void;
}) {
  if (query.isPending) {
    return (
      <XStack center className={`${LIST_FRAME} gap-2 px-4 py-4`}>
        <Icons.loader className="size-3.5 animate-spin text-foreground-muted" />
        <YStack className="gap-0.5">
          <span className="text-xs text-foreground-muted">
            Loading accounts from Mail…
          </span>
          <span className="text-xs text-foreground-subtle">
            Littlebird may open Mail to read account names.
          </span>
        </YStack>
      </XStack>
    );
  }

  const result = query.data;
  if (result && !result.ok && result.problem === 'full_disk_access') {
    return (
      <YStack className="items-start gap-3">
        <FullDiskAccessHint granted={false} />
        <Button
          variant="secondary"
          size="sm"
          isLoading={query.isFetching}
          onClick={() => void query.refetch()}
        >
          Try again
        </Button>
      </YStack>
    );
  }
  if (!result || !result.ok) {
    return (
      <YStack
        className={`${LIST_FRAME} items-center gap-3 px-4 py-6 text-center`}
      >
        <p className="text-xs text-foreground-muted">
          Couldn&apos;t load accounts from Mail.
        </p>
        <Button
          variant="secondary"
          size="sm"
          isLoading={query.isFetching}
          onClick={() => void query.refetch()}
        >
          Try again
        </Button>
      </YStack>
    );
  }

  const { accounts, namesUnavailable } = result;
  const allOff =
    accounts.length > 0 &&
    accounts.every((account) => blockedIds.has(account.id.toUpperCase()));

  return (
    <YStack className="gap-2">
      <XStack className="items-baseline gap-1.5">
        <span className="text-xs font-medium text-foreground-muted">
          Accounts
        </span>
        {accounts.length > 0 && (
          <span className="text-xs text-foreground-subtle">
            {accounts.length}
          </span>
        )}
      </XStack>
      {accounts.length === 0 ? (
        <ListMessage>No Mail accounts found on this Mac.</ListMessage>
      ) : (
        <YStack
          className={`${LIST_FRAME} divide-y divide-border overflow-hidden dark:divide-border-strong`}
        >
          {accounts.map((account) => {
            const title = accountTitle(account);
            const detail = accountDetail(account);
            return (
              <XStack
                key={account.id}
                className="items-center gap-3 px-4 py-2.5"
              >
                <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                  {title}
                </span>
                {detail && (
                  <span className="min-w-0 shrink truncate text-xs text-foreground-muted">
                    {detail}
                  </span>
                )}
                <Switch
                  aria-label={`Use ${title}${detail ? ` (${detail})` : ''} with Littlebird`}
                  checked={!blockedIds.has(account.id.toUpperCase())}
                  disabled={saving}
                  onCheckedChange={(enabled) => onToggle(account.id, enabled)}
                />
              </XStack>
            );
          })}
        </YStack>
      )}
      {allOff && (
        <p className="text-xs text-foreground-muted">
          All accounts are off. Littlebird can&apos;t read any of your mail.
        </p>
      )}
      {namesUnavailable && (
        <p className="text-xs text-foreground-muted">
          Allow Littlebird to control Mail in System Settings → Privacy &amp;
          Security → Automation to see account names.
        </p>
      )}
    </YStack>
  );
}

export function AppleMailConfiguration({
  context,
}: {
  context: ConfigurationContext;
}) {
  const serverSlug =
    context.localSource?.kind === 'local_mcp'
      ? context.localSource.serverSlug
      : undefined;
  const serversQuery = useMcpServersQuery();
  const server = serversQuery.data?.find((entry) => entry.slug === serverSlug);

  const fdaQuery = useQuery(fullDiskAccessPermissionQueryOptions());
  const granted = fdaQuery.data === true;

  const setupProgress = useSetupProgress();
  // A Full Disk Access failure is retried once per mount after access is granted
  const retriedFixableFailure = useRef(false);
  const fixableFailure =
    granted && server !== undefined && isFullDiskAccessSetupFailure(server);
  const running = serverSlug !== undefined && serverSlug in setupProgress;
  useEffect(() => {
    if (!fixableFailure || running || !serverSlug) return;
    if (retriedFixableFailure.current) return;
    retriedFixableFailure.current = true;
    void electronApis.axonMcp.runSetup(serverSlug);
  }, [fixableFailure, running, serverSlug]);

  const accountsQuery = useMailAccountsQuery(serverSlug, granted);
  const toggle = useMailAccountToggle(serverSlug);

  if (serversQuery.isPending || fdaQuery.isPending || !server) return null;

  return (
    <YStack className="gap-4 p-6">
      {granted ? (
        <>
          <MailAccountsList
            query={accountsQuery}
            blockedIds={toggle.blockedIds}
            saving={toggle.saving}
            onToggle={toggle.setEnabled}
          />
          <ServerSetupSection
            server={server}
            actionLabel="Rebuild index"
            runningLabel="Rebuilding…"
          />
        </>
      ) : (
        <FullDiskAccessHint granted={false} />
      )}
    </YStack>
  );
}
